import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Flower, Home, Heart, Info, Check, Phone } from "lucide-react";

const Aftercare = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="text-center"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            >
              Aftercare Services
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              There are a few different options for aftercare of your loved pet.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-12">
            {/* Home Burial */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="card"
            >
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Home className="w-10 h-10 text-secondary-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Home Burial
                </h2>
              </div>

              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 text-lg leading-relaxed">
                  If you wish to bury your pet in the garden we will leave them
                  in your care after they have passed. Please ensure you follow
                  local regulations for pet burial.
                </p>
              </div>
            </motion.div>

            {/* Cremation */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="card"
            >
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Flower className="w-10 h-10 text-primary-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Cremation
                </h2>
              </div>

              <div className="prose prose-lg max-w-none space-y-6">
                <p className="text-gray-700 text-lg leading-relaxed">
                  When you are ready we can take your pet directly to the
                  crematorium or you can arrange for your preferred cremation
                  company to come and collect your pet.
                </p>

                <div className="bg-primary-50 rounded-lg p-6">
                  <h3 className="font-semibold text-primary-800 mb-3 text-lg">
                    Shared Cremation
                  </h3>
                  <p className="text-primary-700">
                    Shared cremation is used if you prefer not to have your pets
                    ashes returned.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-800 mb-3 text-lg">
                    Individual Cremation
                  </h3>
                  <p className="text-gray-700">
                    This is a guaranteed single cremation so only your pet's
                    ashes are returned to you. Prices vary slightly with
                    location and can be discussed with me before making a
                    booking.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Need Support or Guidance?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              I'm here to provide compassionate support and guidance during this
              difficult time. Every consultation is confidential and
              understanding.
            </p>
            <div className="flex justify-center">
              <a
                href="/contact#contact-form"
                className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-medium py-3 px-8 rounded-lg transition-all duration-200 text-center"
              >
                Contact Me
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Aftercare;
